import type { AppAggregate, AppBlock, AppField, AppFilter } from './types/app-spec.ts';

/**
 * F6b — the figures and charts of an app, computed from the objects its reader
 * already sees (same access as the list): no new data path, no query from the
 * model. An object counts once; a value that is not a number is not averaged.
 */
export interface AggregatableObject {
  status: string;
  assignee: string | null;
  owner: string | null;
  cells: Array<{ key: string; value: unknown }>;
}

export const fieldValue = (object: AggregatableObject, field: AppField): unknown => {
  if (field === 'status') return object.status;
  if (field === 'assignee') return object.assignee;
  if (field === 'owner') return object.owner;
  return object.cells.find((cell) => cell.key === field)?.value ?? null;
};

/** 2, "2", "2 / 3", "2,5" → a number; anything else → null. */
export const numericValue = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^-?\d+(?:[.,]\d+)?/);
  return match ? Number(match[0].replace(',', '.')) : null;
};

const label = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  return typeof value === 'string' ? value.trim() || null : String(value);
};

export const applyFilters = <T extends AggregatableObject>(objects: T[], filters: AppFilter[] | undefined): T[] =>
  !filters?.length ? objects : objects.filter((object) =>
    filters.every((filter) => filter.in.includes(label(fieldValue(object, filter.field)) ?? '')));

const reduce = (op: 'avg' | 'sum' | 'min' | 'max', values: number[]): number | null => {
  if (values.length === 0) return null;
  if (op === 'sum') return values.reduce((sum, value) => sum + value, 0);
  if (op === 'min') return Math.min(...values);
  if (op === 'max') return Math.max(...values);
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

export const aggregate = (objects: AggregatableObject[], measure: AppAggregate): number | null => {
  if (measure.op === 'count') return objects.length;
  return reduce(measure.op, objects.flatMap((object) => {
    const value = numericValue(fieldValue(object, measure.field));
    return value === null ? [] : [value];
  }));
};

export interface AppChartData {
  categories: string[];
  series: Array<{ key: string; label: string; values: Array<number | null> }>;
}

/**
 * Categories in the order they appear (or of the listed fields); one series per
 * value of `series` in order of appearance, or a single series.
 */
export const chartData = (
  objects: AggregatableObject[],
  block: Extract<AppBlock, { type: 'chart' }>,
  labelOf: (field: AppField, value: string) => string = (_field, value) => value,
): AppChartData => {
  const visible = applyFilters(objects, block.filter);
  const groups: Array<{ key: string; label: string; objects: AggregatableObject[] }> = [];
  if (block.series) {
    for (const object of visible) {
      const key = label(fieldValue(object, block.series));
      if (key === null) continue;
      const group = groups.find((item) => item.key === key);
      if (group) group.objects.push(object);
      else groups.push({ key, label: labelOf(block.series, key), objects: [object] });
    }
  } else {
    groups.push({ key: 'all', label: block.label, objects: visible });
  }

  if (block.x.by === 'fields') {
    const op = block.aggregate.op === 'count' ? 'avg' : block.aggregate.op;
    const fields = block.x.fields;
    return {
      categories: fields.map((item) => item.label),
      series: groups.map((group) => ({
        key: group.key, label: group.label,
        values: fields.map((item) => aggregate(group.objects, { op, field: item.field })),
      })),
    };
  }

  const xField = block.x.field;
  const keys: string[] = [];
  for (const object of visible) {
    const key = label(fieldValue(object, xField));
    if (key !== null && !keys.includes(key)) keys.push(key);
  }
  const measure: AppAggregate = 'field' in block.aggregate ? block.aggregate : { op: 'count' };
  return {
    categories: keys.map((key) => labelOf(xField, key)),
    series: groups.map((group) => ({
      key: group.key, label: group.label,
      values: keys.map((key) => {
        const inCategory = group.objects.filter((object) => label(fieldValue(object, xField)) === key);
        return inCategory.length ? aggregate(inCategory, measure) : null;
      }),
    })),
  };
};
